import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { formatISO } from 'date-fns';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../../core/auth/user-auth/store/actions/login-logout.action';
import { ArticleActions } from '../../../../core/catalog/article/store/actions';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../../core/global-message';
import { GlobalMessageActions } from '../../../../core/global-message/store';
import { AreaOfUse, AreaOfUseList } from '../../../../core/model/area-of-use.model';
import { CurrenciesActionTypes } from '../../../../core/site-context/store/actions/currencies.action';
import { LanguagesActionTypes } from '../../../../core/site-context/store/actions/languages.action';
import { UserActions } from '../../../../core/user/store/actions';
import { UpdateAreaOfUseSyncDatesForFeedData } from '../../../../core/user/store/actions/feed.action';
import { normalizeHttpError, withdrawOn } from '../../../../core/util';
import { AppActions } from '../../../../core/util/store/actions';
import { AreaOfUseAdminConnector } from '../../connectors/area-of-use-admin-connector';
import { AreaOfUseAdminService } from '../../services';
import { AreaOfUseAdminActions } from '../actions';
import { getAllAreaOfUseCategories } from '../util/area-of-use-admin-utils';

@Injectable()
export class AreaOfUseAdminEffects {
  private contextChange$ = this.actions$.pipe(
    ofType(CurrenciesActionTypes.CurrencyChange, LanguagesActionTypes.LanguageChange, LoginLogoutActionTypes.Logout)
  );

  loadAreaOfUseListAdmin$: Observable<
    | AreaOfUseAdminActions.LoadAreaOfUseListAdminSuccess
    | AreaOfUseAdminActions.LoadAreaOfUseListAdminFail
    | AreaOfUseAdminActions.LoadAreaOfUseCategoryAdminSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.LoadAreaOfUseListAdmin),
      map((action: AreaOfUseAdminActions.LoadAreaOfUseListAdmin) => action.payload),
      switchMap((payload) =>
        this.connector.loadAreaOfUseList(payload).pipe(
          switchMap((data: AreaOfUseList) => {
            return [
              new AreaOfUseAdminActions.LoadAreaOfUseCategoryAdminSuccess(getAllAreaOfUseCategories(data.areasOfUse)),
              new AreaOfUseAdminActions.LoadAreaOfUseListAdminSuccess(data),
            ];
          }),
          catchError((error) => of(new AreaOfUseAdminActions.LoadAreaOfUseListAdminFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForLoadAreaOfUseListAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.LoadAreaOfUseListAdminFail),
        map((action: AreaOfUseAdminActions.LoadAreaOfUseListAdminFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.loadAreaOfUseFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  loadAreasOfUseByProduct$: Observable<
    | AreaOfUseAdminActions.LoadAreaOfUseCategoryAdminSuccess
    | AreaOfUseAdminActions.LoadAreasOfUseForProductFail
    | AreaOfUseAdminActions.LoadAreasOfUseForProductSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.LoadAreasOfUseForProduct),
      map((action: AreaOfUseAdminActions.LoadAreasOfUseForProduct) => action.payload),
      switchMap((payload) =>
        this.connector.loadProductAreasOfUse(payload.userId, payload.productCode).pipe(
          switchMap((data: AreaOfUseList) => {
            return [
              new AreaOfUseAdminActions.LoadAreaOfUseCategoryAdminSuccess(data.areasOfUse),
              new AreaOfUseAdminActions.LoadAreasOfUseForProductSuccess(data),
            ];
          }),
          catchError((error) => of(new AreaOfUseAdminActions.LoadAreasOfUseForProductFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForLoadAreasOfUseByProduct$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.LoadAreasOfUseForProductFail),
        map((action: AreaOfUseAdminActions.LoadAreasOfUseForProductFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.loadAreaOfUseFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  excludeArticlesFromAreaOfUse$: Observable<
    AreaOfUseAdminActions.ExcludeArticlesFromAreaOfUseFail | AreaOfUseAdminActions.ExcludeArticlesFromAreaOfUseSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.ExcludeArticlesFromAreaOfUse),
      map((action: AreaOfUseAdminActions.ExcludeArticlesFromAreaOfUse) => action.payload),
      switchMap((payload) =>
        this.connector.excludeArticlesFromAreaOfUse(payload.userId, payload.categoryCode, payload.excludedArticleCodes).pipe(
          map(() => new AreaOfUseAdminActions.ExcludeArticlesFromAreaOfUseSuccess(payload)),
          catchError((error) => of(new AreaOfUseAdminActions.ExcludeArticlesFromAreaOfUseFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleSuccessForExcludeArticlesFromAreaOfUse$: Observable<
    GlobalMessageActions.AddMessage | ArticleActions.LoadArticleReset | AppActions.NoOpAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.ExcludeArticlesFromAreaOfUseSuccess),
      map((action: AreaOfUseAdminActions.ExcludeArticlesFromAreaOfUseSuccess) => action.payload),
      mergeMap(({ changedArticleCodes }) => {
        const actions: Array<GlobalMessageActions.AddMessage | ArticleActions.LoadArticleReset> =
          changedArticleCodes?.map((code) => new ArticleActions.LoadArticleReset(code)) || [];

        actions.push(
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'areaOfUse.articlesVisibilityWasUpdatedSuccessfully_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
        );

        return actions;
      })
    )
  );

  addProductToAreaOfUse$: Observable<
    AreaOfUseAdminActions.AddProductToAreaOfUseFail | AreaOfUseAdminActions.AddProductToAreaOfUseSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.AddProductToAreaOfUse),
      map((action: AreaOfUseAdminActions.AddProductToAreaOfUse) => action.payload),
      switchMap((payload) =>
        this.connector.addProductToAreaOfUse(payload.userId, payload.productCode, payload.categoryCode).pipe(
          map(() => new AreaOfUseAdminActions.AddProductToAreaOfUseSuccess(payload)),
          catchError((error) => of(new AreaOfUseAdminActions.AddProductToAreaOfUseFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForAddProductToAreaOfUse$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.AddProductToAreaOfUseFail),
      map((action: AreaOfUseAdminActions.AddProductToAreaOfUseFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'areaOfUse.addProductFromAreaOfUseFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  handleSuccessForAddProductToAreaOfUse$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.AddProductToAreaOfUseSuccess),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'areaOfUse.addProductFromAreaOfUseSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      )
    )
  );

  removeProductsFromAreaOfUse$: Observable<
    AreaOfUseAdminActions.RemoveProductsFromAreaOfUseFail | AreaOfUseAdminActions.RemoveProductsFromAreaOfUseSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.RemoveProductsFromAreaOfUse),
      map((action: AreaOfUseAdminActions.RemoveProductsFromAreaOfUse) => action.payload),
      switchMap((payload) =>
        this.connector.removeProductsFromAreaOfUse(payload.userId, payload.productCodes, payload.categoryCode).pipe(
          map(() => new AreaOfUseAdminActions.RemoveProductsFromAreaOfUseSuccess(payload)),
          catchError((error) => of(new AreaOfUseAdminActions.RemoveProductsFromAreaOfUseFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForRemoveProductsFromAreaOfUse$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.RemoveProductsFromAreaOfUseFail),
        map((action: AreaOfUseAdminActions.RemoveProductsFromAreaOfUseFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.removeProductFromAreaOfUseFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
    );

  handleSuccessForRemoveProductsFromAreaOfUse$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.RemoveProductsFromAreaOfUseSuccess),
        map(
          () =>
            new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.removeProductFromAreaOfUseSuccess_message',
              },
              type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
            })
        )
      )
    );

  createAreaOfUseCategoryAdmin$: Observable<
    AreaOfUseAdminActions.CreateAreaOfUseCategoryAdminSuccess | AreaOfUseAdminActions.CreateAreaOfUseCategoryAdminFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.CreateAreaOfUseCategoryAdmin),
      map((action: AreaOfUseAdminActions.CreateAreaOfUseCategoryAdmin) => action.payload),
      switchMap((payload) =>
        this.connector.createAreaOfUseCategory(payload.userId, payload.areaOfUseName, payload.parentAreaOfUseCode).pipe(
          map(
            (areaOfUseCategory: AreaOfUse) =>
              new AreaOfUseAdminActions.CreateAreaOfUseCategoryAdminSuccess({ ...payload, areaOfUseCategory })
          ),
          catchError((error) =>
            of(
              new AreaOfUseAdminActions.CreateAreaOfUseCategoryAdminFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleSuccessForCreateAreaOfUseCategoryAdmin$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.CreateAreaOfUseCategoryAdminSuccess),
      tap(() => this.areaOfUseAdminService.loadAreaOfUseCategories()),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'areaOfUse.createSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForCreateAreaOfUseCategoryAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.CreateAreaOfUseCategoryAdminFail),
        map((action: AreaOfUseAdminActions.CreateAreaOfUseCategoryAdminFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.createFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
    );

  deleteAreaOfUseCategoryAdmin$: Observable<
    AreaOfUseAdminActions.DeleteAreaOfUseCategoryAdminSuccess | AreaOfUseAdminActions.DeleteAreaOfUseCategoryAdminFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.DeleteAreaOfUseCategoryAdmin),
      map((action: AreaOfUseAdminActions.DeleteAreaOfUseCategoryAdmin) => action.payload),
      switchMap((payload) =>
        this.connector.deleteAreaOfUseCategory(payload.userId, payload.code).pipe(
          map(() => new AreaOfUseAdminActions.DeleteAreaOfUseCategoryAdminSuccess(payload)),
          catchError((error) =>
            of(
              new AreaOfUseAdminActions.DeleteAreaOfUseCategoryAdminFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForDeleteAreaOfUseCategoryAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.DeleteAreaOfUseCategoryAdminFail),
        map((action: AreaOfUseAdminActions.DeleteAreaOfUseCategoryAdminFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.removeFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
    );

  handleSuccessForDeleteAreaOfUseCategoryAdmin$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.DeleteAreaOfUseCategoryAdminSuccess),
      map((action: AreaOfUseAdminActions.DeleteAreaOfUseCategoryAdminSuccess) => action.payload),
      tap(() => this.areaOfUseAdminService.loadAreaOfUseCategories()),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'areaOfUse.removeSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      )
    )
  );

  editAreaOfUseCategoryAdmin$: Observable<
    AreaOfUseAdminActions.EditAreaOfUseCategoryAdminSuccess | AreaOfUseAdminActions.EditAreaOfUseCategoryAdminFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.EditAreaOfUseCategoryAdmin),
      map((action: AreaOfUseAdminActions.EditAreaOfUseCategoryAdmin) => action.payload),
      mergeMap((payload) =>
        this.connector.editAreaOfUseCategory(payload.userId, payload.code, payload.params).pipe(
          map(
            (areaOfUseCategory: AreaOfUse) =>
              new AreaOfUseAdminActions.EditAreaOfUseCategoryAdminSuccess({
                areaOfUse: areaOfUseCategory,
                requiresListReload: payload.requiresListReload,
              })
          ),
          catchError((error) =>
            of(
              new AreaOfUseAdminActions.EditAreaOfUseCategoryAdminFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleSuccessForEditAreaOfUseCategoryAdmin$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.EditAreaOfUseCategoryAdminSuccess),
      map((action: AreaOfUseAdminActions.EditAreaOfUseCategoryAdminSuccess) => action.payload),
      tap((payload) => {
        if (payload.requiresListReload) {
          this.areaOfUseAdminService.loadAreaOfUseCategories();
        }
      }),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'areaOfUse.updateSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      )
    )
  );

  handleFailureForEditAreaOfUseCategoryAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.EditAreaOfUseCategoryAdminFail),
        map((action: AreaOfUseAdminActions.EditAreaOfUseCategoryAdminFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.updateFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  publishAreasOfUse$: Observable<
    AreaOfUseAdminActions.PublishAreasOfUseAdminFail | AreaOfUseAdminActions.PublishAreasOfUseAdminSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.PublishAreasOfUseAdmin),
      map((action: AreaOfUseAdminActions.PublishAreasOfUseAdmin) => action.payload),
      switchMap((payload) =>
        this.connector.publishAreasOfUse(payload).pipe(
          map(() => new AreaOfUseAdminActions.PublishAreasOfUseAdminSuccess()),
          catchError((error: HttpErrorResponse) =>
            of(
              new AreaOfUseAdminActions.PublishAreasOfUseAdminFail({
                error: normalizeHttpError(error),
              })
            )
          )
        )
      )
    )
  );

  handleFailureForPublishAreasOfUse$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.PublishAreasOfUseAdminFail),
      map((action: AreaOfUseAdminActions.PublishAreasOfUseAdminFail) => {
        if (!isErrorGloballyHandled(action.payload.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'areaOfUse.publishAreaOfUseFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  handleSuccessForPublishAreasOfUse$: Observable<GlobalMessageActions.AddMessage | UpdateAreaOfUseSyncDatesForFeedData> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.PublishAreasOfUseAdminSuccess),
        switchMap(() => {
          return [
            new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.publishAreaOfUseSuccess_message',
              },
              type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
            }),
            new UserActions.UpdateAreaOfUseSyncDatesForFeedData({
              lastAreaOfUseSyncStartTime: formatISO(new Date()),
              lastAreaOfUseSyncEndTime: undefined,
            }),
          ];
        })
      )
    );

  loadAreaOfUseCategoryDetailsAdmin$: Observable<
    | AreaOfUseAdminActions.LoadAreaOfUseCategoryDetailsSuccess
    | AreaOfUseAdminActions.LoadAreaOfUseCategoryDetailsFail
    | AreaOfUseAdminActions.LoadAreaOfUseCategoryAdminSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.LoadAreaOfUseCategoryDetails),
      map((action: AreaOfUseAdminActions.LoadAreaOfUseCategoryDetails) => action.payload),
      switchMap((payload) =>
        this.connector.getAreaOfUseCategoryDetails(payload.userId, payload.code).pipe(
          switchMap((data: AreaOfUse) => {
            return [
              new AreaOfUseAdminActions.LoadAreaOfUseCategoryAdminSuccess(data),
              new AreaOfUseAdminActions.LoadAreaOfUseCategoryDetailsSuccess(data),
            ];
          }),
          catchError((error) => of(new AreaOfUseAdminActions.LoadAreaOfUseCategoryDetailsFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForLoadAreaOfUseCategoryDetailsAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.LoadAreaOfUseCategoryDetailsFail),
        map((action: AreaOfUseAdminActions.LoadAreaOfUseCategoryDetailsFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.loadAreaOfUseCategoryDetailsFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
    );

  replaceAreaOfUseProduct$: Observable<
    | AreaOfUseAdminActions.ReplaceAreaOfUseAdminProductSuccess
    | AreaOfUseAdminActions.ReplaceAreaOfUseAdminProductFail
    | GlobalMessageActions.AddMessage
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.ReplaceAreaOfUseAdminProduct),
      switchMap((action: AreaOfUseAdminActions.ReplaceAreaOfUseAdminProduct) =>
        this.connector.replaceAreaOfUseProduct(action.userId, action.code, action.oldProductCode, action.newProductCode).pipe(
          concatMap(() => [
            new AreaOfUseAdminActions.ReplaceAreaOfUseAdminProductSuccess(
              action.code,
              action.oldProductCode,
              action.newProductCode
            ),
            new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.replaceAreaOfUseProductSuccess_message',
                uid: `${action.code}-${action.newProductCode}`,
              },
              type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
            }),
          ]),
          catchError((error) => of(new AreaOfUseAdminActions.ReplaceAreaOfUseAdminProductFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForReplaceAreaOfUseProduct$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AreaOfUseAdminActions.AreaOfUseAdminActionTypes.ReplaceAreaOfUseAdminProductFail),
        map((action: AreaOfUseAdminActions.ReplaceAreaOfUseAdminProductFail) => {
          if (!isErrorGloballyHandled(action.payload?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'areaOfUse.replaceAreaOfUseProductFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  constructor(
    private actions$: Actions,
    private connector: AreaOfUseAdminConnector,
    private areaOfUseAdminService: AreaOfUseAdminService
  ) {}
}
